import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { uid } from "@/lib/uid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Trash2, TrendingUp, ClipboardList } from "lucide-react";

const PRIORITA_CLR = {
  urgente: "bg-rose-600 text-white",
  alta: "bg-amber-500 text-white",
  media: "bg-sky-500 text-white",
  bassa: "bg-slate-500 text-white",
};

export default function StepPianoMiglioramento({ dvr, data, onChange, aiLoading, setAiLoading }) {
  // Supports both legacy list [] and new object {piano:[], sintesi:"", raccolta_grezza:[]}
  const initial = Array.isArray(data)
    ? { piano: data, sintesi: "", raccolta_grezza: [] }
    : data && typeof data === "object"
    ? data
    : { piano: [], sintesi: "", raccolta_grezza: [] };
  const d = initial;
  const piano = d.piano || [];

  const [nuovo, setNuovo] = useState({ azione: "", responsabile: "", priorita: "media", tempistica: "" });

  const runAuto = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/dvr/ai/piano-miglioramento-auto", { dvr_id: dvr.id }, { timeout: 120000 });
      onChange(r.data);
      toast.success(`Piano generato: ${r.data.piano?.length || 0} azioni`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    }
    setAiLoading(false);
  };

  const addManuale = () => {
    if (!nuovo.azione.trim()) return;
    const next = { ...d, piano: [...piano, { ...nuovo, _uid: uid(), source: "Manuale" }] };
    onChange(next);
    setNuovo({ azione: "", responsabile: "", priorita: "media", tempistica: "" });
  };

  const remove = (i) => {
    onChange({ ...d, piano: piano.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="space-y-4" data-testid="step-piano">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground flex-1 min-w-[250px]">
          Il piano di miglioramento raccoglie automaticamente le azioni prioritarie da tutti gli step
          del DVR (Incendio, VDT, Attrezzature, Sostanze, Sorveglianza, Segnaletica…) e le organizza
          per priorità. Puoi aggiungere anche azioni manuali.
        </p>
        <Button onClick={runAuto} disabled={aiLoading} className="rounded-full" data-testid="pm-auto">
          {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
          Aggrega automaticamente
        </Button>
      </div>

      {d.sintesi && (
        <div className="border border-border rounded-md p-3 bg-muted/20 text-sm italic">{d.sintesi}</div>
      )}

      <div className="border border-border rounded-md p-3 bg-muted/10">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Aggiungi azione manuale</div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
          <Input placeholder="Azione" value={nuovo.azione} onChange={(e) => setNuovo({ ...nuovo, azione: e.target.value })} className="sm:col-span-2" data-testid="s8-azione" />
          <Input placeholder="Responsabile" value={nuovo.responsabile} onChange={(e) => setNuovo({ ...nuovo, responsabile: e.target.value })} />
          <select value={nuovo.priorita} onChange={(e) => setNuovo({ ...nuovo, priorita: e.target.value })} className="border border-border rounded-md px-3 py-2 text-sm bg-background">
            <option value="urgente">Urgente (&lt;15gg)</option>
            <option value="alta">Alta (1 mese)</option>
            <option value="media">Media (6 mesi)</option>
            <option value="bassa">Bassa (12 mesi)</option>
          </select>
          <Input placeholder="Tempistica" value={nuovo.tempistica} onChange={(e) => setNuovo({ ...nuovo, tempistica: e.target.value })} />
        </div>
        <Button onClick={addManuale} size="sm" className="mt-2 rounded-full" data-testid="s8-add">
          <Plus className="w-4 h-4 mr-1" /> Aggiungi
        </Button>
      </div>

      {piano.length > 0 ? (
        <div className="border border-border rounded-md overflow-hidden">
          <div className="bg-muted/30 px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest">
            <ClipboardList className="w-4 h-4" />
            Piano ({piano.length} azion{piano.length === 1 ? "e" : "i"})
          </div>
          <ul className="divide-y divide-border">
            {piano.map((it, i) => (
              <li key={it._uid || `piano-${i}-${(it.azione || "").slice(0, 20)}`} className="p-3 flex items-start gap-3 text-sm">
                <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${PRIORITA_CLR[it.priorita] || "bg-slate-300"}`}>
                  {(it.priorita || "media").toUpperCase()}
                </span>
                <div className="flex-1">
                  <div>{it.azione}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {it.source && <span className="mr-2">📌 {it.source}</span>}
                    {it.responsabile && <span className="mr-2">👤 {it.responsabile}</span>}
                    {it.tempistica && <span>⏱ {it.tempistica}</span>}
                  </div>
                  {it.normativa && <div className="text-[11px] text-muted-foreground">Rif: {it.normativa}</div>}
                </div>
                <button onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive" data-testid={`s8-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground italic border border-dashed border-border rounded p-6 text-center">
          Nessuna azione. Clicca "Aggrega automaticamente" per estrarre dai moduli DVR.
        </div>
      )}
    </div>
  );
}
